import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2 } from 'lucide-react';
import { clsx } from 'clsx';

import { useAuth } from '../../../contexts/AuthContext';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { formatRelativeTime } from '../../../utils/format';
import { getSupportBasePath } from '../constants';
import { useSupportNotifications } from '../hooks/useSupportNotifications';
import type { SupportNotification } from '../types';

function notificationPreview(message: string): string {
  const line = message.split('\n').find((part) => part.trim() && !part.startsWith('SUP-'));
  return (line ?? message).trim().slice(0, 120);
}

interface SupportNotificationBellProps {
  className?: string;
}

export const SupportNotificationBell: React.FC<SupportNotificationBellProps> = ({ className }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = getSupportBasePath(user?.roles ?? []);
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const {
    unread,
    items,
    loadingList,
    refreshList,
    markRead,
    markAllRead,
  } = useSupportNotifications({ enabled: Boolean(user) });

  useEffect(() => {
    if (open) {
      void refreshList();
    }
  }, [open, refreshList]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const handleSelect = useCallback(
    async (notification: SupportNotification) => {
      if (!notification.is_read) {
        try {
          await markRead(notification.id);
        } catch {
          /* still navigate */
        }
      }
      setOpen(false);
      if (notification.ticket_id) {
        navigate(`${basePath}/tickets/${notification.ticket_id}`);
      }
    },
    [basePath, markRead, navigate]
  );

  if (!user) return null;

  return (
    <div ref={panelRef} className={clsx('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="relative p-2 sm:p-2.5 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
        aria-label="Notifications"
        aria-expanded={open}
      >
        <Bell className="w-5 h-5" />
        {unread > 0 ? (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1.125rem] h-[1.125rem] px-1 rounded-full bg-red-500 text-[10px] font-bold text-white flex items-center justify-center ring-2 ring-purple-600">
            {unread > 99 ? '99+' : unread}
          </span>
        ) : null}
      </button>

      {open ? (
        <div className="absolute right-0 mt-2 w-[min(22rem,calc(100vw-1rem))] z-[60] rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden">
          <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-slate-100 dark:border-gray-800">
            <p className="text-sm font-semibold text-slate-900 dark:text-gray-100">Notifications</p>
            {unread > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs gap-1 h-8"
                onClick={() => void markAllRead()}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </Button>
            ) : null}
          </div>

          <div className="max-h-[min(24rem,60vh)] overflow-y-auto">
            {loadingList ? (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-slate-500 dark:text-gray-400">
                No notifications yet
              </p>
            ) : (
              <ul className="divide-y divide-slate-100 dark:divide-gray-800">
                {items.map((notification) => (
                  <li key={notification.id}>
                    <button
                      type="button"
                      onClick={() => void handleSelect(notification)}
                      className={clsx(
                        'w-full text-left px-4 py-3 transition hover:bg-slate-50 dark:hover:bg-gray-800/80',
                        !notification.is_read && 'bg-primary-50/60 dark:bg-primary-950/30'
                      )}
                    >
                      <div className="flex items-start gap-2">
                        {!notification.is_read ? (
                          <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary-500" aria-hidden />
                        ) : (
                          <span className="mt-1.5 h-2 w-2 shrink-0" aria-hidden />
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-slate-900 dark:text-gray-100 truncate">
                            {notification.title}
                          </p>
                          {notification.ticket_number ? (
                            <p className="text-xs font-mono text-primary-700 dark:text-primary-300">
                              {notification.ticket_number}
                            </p>
                          ) : null}
                          <p className="text-xs text-slate-600 dark:text-gray-400 line-clamp-2 mt-0.5">
                            {notificationPreview(notification.message)}
                          </p>
                          {notification.created_at ? (
                            <p className="text-[11px] text-slate-400 dark:text-gray-500 mt-1">
                              {formatRelativeTime(notification.created_at)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

interface SupportNotificationsPanelProps {
  className?: string;
}

export const SupportNotificationsPanel: React.FC<SupportNotificationsPanelProps> = ({ className }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const basePath = getSupportBasePath(user?.roles ?? []);
  const { unread, items, loadingList, refreshList, markRead, markAllRead } = useSupportNotifications({
    enabled: Boolean(user),
  });

  useEffect(() => {
    void refreshList();
  }, [refreshList]);

  const handleSelect = async (notification: SupportNotification) => {
    if (!notification.is_read) {
      try {
        await markRead(notification.id);
      } catch {
        /* navigate anyway */
      }
    }
    if (notification.ticket_id) {
      navigate(`${basePath}/tickets/${notification.ticket_id}`);
    }
  };

  const unreadItems = items.filter((n) => !n.is_read);
  const recentItems = items.filter((n) => n.is_read).slice(0, 5);

  return (
    <div className={className}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Bell className="h-5 w-5 text-primary-600" />
          Notifications
          {unread > 0 ? <Badge variant="error" size="sm">{unread}</Badge> : null}
        </h2>
        {unread > 0 ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => void markAllRead()}>
            Mark all read
          </Button>
        ) : null}
      </div>

      {loadingList ? (
        <div className="flex justify-center py-6 text-slate-500">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <NotificationSection
            title="Unread"
            emptyLabel="You're all caught up"
            notifications={unreadItems}
            onSelect={handleSelect}
          />
          <NotificationSection
            title="Recent"
            emptyLabel="No recent notifications"
            notifications={recentItems}
            onSelect={handleSelect}
          />
        </div>
      )}
    </div>
  );
};

function NotificationSection({
  title,
  emptyLabel,
  notifications,
  onSelect,
}: {
  title: string;
  emptyLabel: string;
  notifications: SupportNotification[];
  onSelect: (n: SupportNotification) => void;
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
      <p className="text-sm font-semibold text-slate-800 dark:text-gray-200 mb-3">{title}</p>
      {notifications.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-gray-400">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2">
          {notifications.map((notification) => (
            <li key={notification.id}>
              <button
                type="button"
                onClick={() => void onSelect(notification)}
                className="w-full text-left rounded-lg px-3 py-2 hover:bg-slate-50 dark:hover:bg-gray-800 transition"
              >
                <p className="text-sm font-medium text-slate-900 dark:text-gray-100">{notification.title}</p>
                <p className="text-xs text-slate-500 dark:text-gray-400 line-clamp-1">
                  {notificationPreview(notification.message)}
                </p>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
